import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { PostHogPageviewTracker } from '@lightning/analytics';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Layout from './components/Layout';
import RequireOnboarding from './components/RequireOnboarding';
import ChatPage from './pages/ChatPage';
import GoogleCallbackPage from './pages/GoogleCallbackPage';
import DojoPage from './pages/DojoPage';
import DojoRoundReviewPage from './pages/DojoRoundReviewPage';
import DojoReviewPage from './pages/DojoReviewPage';
import GraphExplorerPage from './pages/GraphExplorerPage';
import HistoryPage from './pages/HistoryPage';
import CompanionSessionPage from './pages/CompanionSessionPage';
import CompanionPrivacyPage from './pages/CompanionPrivacyPage';
import CompanionDownloadPage from './pages/CompanionDownloadPage';
import LoginPage from './pages/LoginPage';
import LandingPage from './pages/LandingPage';
import StoryPage from './pages/StoryPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import OnboardingPage from './pages/OnboardingPage';
import YourHowPage from './pages/YourHowPage';
import SettingsPage from './pages/SettingsPage';
import PausePage from './pages/PausePage';

const BYPASS_AUTH = import.meta.env.VITE_BYPASS_AUTH === 'true';
if (BYPASS_AUTH) {
  // Loud warning so an accidental prod build with this flag set is hard to miss.
  console.warn(
    '[Howie] VITE_BYPASS_AUTH=true — all protected routes are open. ' +
    'This must NEVER be set in production builds.',
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (BYPASS_AUTH) return <>{children}</>;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="spinner" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function HomeRoute() {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center min-h-screen"><div className="spinner" /></div>;
  if (user) return <Navigate to="/chat" replace />;
  return <LandingPage />;
}

function LegacyPracticeRoundRedirect() {
  const { roundId } = useParams();
  return <Navigate to={roundId ? `/dojo/round/${roundId}` : '/dojo'} replace />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<HomeRoute />} />
      <Route path="/story" element={<StoryPage />} />
      <Route path="/login" element={<LoginPage mode="sign-in" />} />
      {/* Google sends the user back here after HOWIE's own consent screen (lib/googleSignIn.ts). */}
      <Route path="/auth/google/callback" element={<GoogleCallbackPage />} />
      <Route path="/sign-in" element={<LoginPage mode="sign-in" />} />
      <Route path="/signup" element={<LoginPage mode="sign-up" />} />
      <Route path="/sign-up" element={<LoginPage mode="sign-up" />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      {/* Signup and email confirmation enter here; RequireOnboarding also sends any
          signed-in account with no onboarding row here. An abandoned local draft
          is resumed on return. */}
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute>
            <OnboardingPage />
          </ProtectedRoute>
        }
      />
      {/* Reviewer reading room for the unit library (vignettes + the authored answer
          key). Signed-in only: the material is authored review content, not for
          learners, so it sits behind auth but outside the onboarding gate. */}
      <Route
        path="/dojo-review"
        element={
          <ProtectedRoute>
            <DojoReviewPage />
          </ProtectedRoute>
        }
      />
      <Route path="/practice-review" element={<Navigate to="/dojo-review" replace />} />
      {/* Privacy policy for the Companion extension; linked from the Chrome Web Store,
          so it must load without signing in. */}
      <Route path="/companion/privacy" element={<CompanionPrivacyPage />} />
      {/* Public download page for the Mac and Windows app. Declared before /companion/:sessionId
          so it never falls through to the signed-in session page. */}
      <Route path="/companion/download" element={<CompanionDownloadPage />} />

      {/* The signed-in shell. A first-time account is routed to /onboarding once
          (Skip is always available); completed and skipped users pass through. */}
      <Route
        element={
          <ProtectedRoute>
            <RequireOnboarding bypass={BYPASS_AUTH}>
              <Layout />
            </RequireOnboarding>
          </ProtectedRoute>
        }
      >
        <Route path="/chat" element={<ChatPage />} />
        <Route path="/pause" element={<PausePage />} />
        <Route path="/companion/:sessionId" element={<CompanionSessionPage />} />
        <Route path="/learn" element={<Navigate to="/dojo" replace />} />
        <Route path="/dojo" element={<DojoPage />} />
        <Route path="/dojo/round/:roundId" element={<DojoRoundReviewPage />} />
        <Route path="/practice" element={<Navigate to="/dojo" replace />} />
        <Route path="/practice/round/:roundId" element={<LegacyPracticeRoundRedirect />} />
        <Route path="/history" element={<HistoryPage />} />
        <Route path="/profile" element={<Navigate to="/your-how" replace />} />
        <Route path="/your-how" element={<YourHowPage />} />
        <Route path="/settings" element={<Navigate to="/settings/general" replace />} />
        <Route path="/settings/:section" element={<SettingsPage />} />
        <Route path="/extension" element={<Navigate to="/settings/extension" replace />} />
        <Route path="/explore" element={<GraphExplorerPage />} />
      </Route>

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <PostHogPageviewTracker />
      <AppRoutes />
    </AuthProvider>
  );
}

export default App;
